import {
  Building2,
  Calendar,
  ChevronRight,
  CreditCard,
  DollarSign,
  LayoutDashboard,
  TrendingUp,
  Upload,
  Users,
  X,
} from 'lucide-react';
import React, { memo, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAppSelector } from '../../store';
import { OpsSelector } from '../OpsSelector';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Skeleton } from '../ui/skeleton';

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path?: string;
  children?: MenuItem[];
  requiredScreen?: string;
}

const menuItems: MenuItem[] = [
  {
    text: 'Início',
    icon: <LayoutDashboard className="h-4 w-4" />,
    path: '/',
  },
  {
    text: 'Status Pagamento',
    icon: <CreditCard className="h-4 w-4" />,
    path: '/romaneio/payment',
    requiredScreen: 'romaneioPagamento',
  },
  {
    text: 'Checkout',
    icon: <CreditCard className="h-4 w-4" />,
    path: '/romaneio/checkout',
    requiredScreen: 'romaneioCheckout',
  },
  {
    text: 'Solicitações',
    icon: <DollarSign className="h-4 w-4" />,
    path: '/romaneio/antecipacao',
    requiredScreen: 'romaneioAntecipacao',
  },
  {
    text: 'Estatísticas',
    icon: <TrendingUp className="h-4 w-4" />,
    path: '/romaneio/statistics',
    requiredScreen: 'romaneioEstatisticas',
  },
  {
    text: 'Usuários',
    icon: <Users className="h-4 w-4" />,
    path: '/romaneio/entregadores',
    requiredScreen: 'romaneioEntregadores',
  },
  {
    text: 'Usuários',
    icon: <Users className="h-4 w-4" />,
    path: '/romaneio/entregadores',
    requiredScreen: 'entregadoresMuve',
  },
  {
    text: 'Usuários',
    icon: <Users className="h-4 w-4" />,
    path: '/romaneio/entregadores-entrego',
    requiredScreen: 'entregadoresEntrego',
  },
  {
    text: 'Gerenciar Usuários',
    icon: <Building2 className="h-4 w-4" />,
    path: '/romaneio/cadastro-entrego',
    requiredScreen: 'romaneioCadastroEntrego',
  },
  {
    text: 'Fechamento Semanal',
    icon: <Calendar className="h-4 w-4" />,
    path: '/romaneio/entrego-fechamento',
    requiredScreen: 'entregoFechamento',
  },
  {
    text: 'Fechamento MUVE',
    icon: <Calendar className="h-4 w-4" />,
    path: '/romaneio/muve-fechamento',
    requiredScreen: 'muveFechamento',
  },
  {
    text: 'Fechamento InDrive',
    icon: <Calendar className="h-4 w-4" />,
    path: '/romaneio/indrive-fechamento',
    requiredScreen: 'indriveFechamento',
  },
  {
    text: 'Pagamentos InDrive',
    icon: <DollarSign className="h-4 w-4" />,
    path: '/romaneio/indrive-pagamento',
    requiredScreen: 'indrivePagamento',
  },
];

interface UploadConfig {
  key: string;
  title: string;
  subtitle?: string;
  doc_type: string;
  extension_file: string;
  upload_title?: string;
}

interface UploadMenuItemProps {
  upload: UploadConfig;
  isActive: boolean;
  onNavigate: (upload: UploadConfig) => void;
}

const UploadMenuItem = memo(function UploadMenuItem({
  upload,
  isActive,
  onNavigate,
}: UploadMenuItemProps) {
  const handleClick = useCallback(() => {
    onNavigate(upload);
  }, [onNavigate, upload]);

  return (
    <Button
      variant="ghost"
      className={cn(
        'w-full justify-start text-left h-10 pl-3 font-normal',
        isActive
          ? 'bg-[hsl(210,35%,32%)] text-white hover:bg-[hsl(210,35%,28%)] hover:text-white'
          : 'text-[hsl(210,50%,10%)] hover:bg-[hsl(210,40%,96%)]'
      )}
      onClick={handleClick}
    >
      <Upload
        className={cn(
          'h-4 w-4',
          isActive ? 'text-white' : 'text-[hsl(215,16%,47%)]'
        )}
      />
      <span className="ml-3 flex-1">{upload.title}</span>
    </Button>
  );
});

interface MenuItemButtonProps {
  item: MenuItem;
  level: number;
  isActive: boolean;
  isExpanded: boolean;
  hasChildren: boolean;
  onClick: () => void;
}

const MenuItemButton = memo(function MenuItemButton({
  item,
  level,
  isActive,
  isExpanded,
  hasChildren,
  onClick,
}: MenuItemButtonProps) {
  const paddingLeft = level === 0 ? 'pl-3' : 'pl-8';

  return (
    <Button
      variant="ghost"
      className={cn(
        'w-full justify-start text-left h-10 font-normal',
        paddingLeft,
        isActive
          ? 'bg-[hsl(210,35%,32%)] text-white hover:bg-[hsl(210,35%,28%)] hover:text-white'
          : 'text-[hsl(210,50%,10%)] hover:bg-[hsl(210,40%,96%)]'
      )}
      onClick={onClick}
    >
      <span className={isActive ? 'text-white' : 'text-[hsl(215,16%,47%)]'}>
        {item.icon}
      </span>
      <span className="ml-3 flex-1">{item.text}</span>
      {hasChildren && (
        <ChevronRight
          className={cn(
            'h-4 w-4 transition-transform',
            isExpanded && 'rotate-90'
          )}
        />
      )}
    </Button>
  );
});

const UploadSkeletonItems = memo(function UploadSkeletonItems() {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <div key={`skeleton-${i}`} className="px-3 py-2">
          <div className="flex items-center gap-3">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-4 flex-1 rounded" />
          </div>
        </div>
      ))}
    </>
  );
});

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  const { currentOps, opsConfigs, fetchingConfigs } = useAppSelector(
    (state) => state.ops
  );

  const currentOpsConfig = useMemo(() => {
    if (!currentOps) {
      return null;
    }
    return opsConfigs[currentOps] || null;
  }, [currentOps, opsConfigs]);

  const isLoadingConfig = useMemo(() => {
    if (!currentOps) {
      return false;
    }
    return fetchingConfigs[currentOps] || false;
  }, [currentOps, fetchingConfigs]);

  const uploads = useMemo(() => {
    return currentOpsConfig?.uploads || [];
  }, [currentOpsConfig?.uploads]);

  const availableScreens = useMemo(() => {
    return currentOpsConfig?.screens || [];
  }, [currentOpsConfig?.screens]);

  const activeUploadDocType = useMemo(() => {
    if (location.pathname !== '/upload') {
      return null;
    }
    const state = location.state as { docType?: string } | null;
    return state?.docType || null;
  }, [location.pathname, location.state]);

  const handleUploadNavigate = useCallback(
    (upload: UploadConfig) => {
      navigate('/upload', {
        state: {
          title: upload.title,
          subtitle: upload.subtitle || `Arquivos para ${upload.title}`,
          docType: upload.doc_type,
          extensionFile: upload.extension_file,
          uploadTitle: upload.upload_title || `Upload de ${upload.title}`,
        },
      });

      if (window.innerWidth < 768) {
        onClose();
      }
    },
    [navigate, onClose]
  );

  const handleItemClick = useCallback(
    (item: MenuItem) => {
      if (item.path) {
        navigate(item.path);
        if (window.innerWidth < 768) {
          onClose();
        }
        return;
      }

      if (item.children) {
        setExpandedItems((prev) => {
          const isExpanded = prev.includes(item.text);
          if (isExpanded) {
            return prev.filter((text) => text !== item.text);
          }
          return [...prev, item.text];
        });
      }
    },
    [navigate, onClose]
  );

  const isItemVisible = useCallback(
    (item: MenuItem): boolean => {
      if (!item.requiredScreen) {
        return true;
      }
      return availableScreens.includes(item.requiredScreen);
    },
    [availableScreens]
  );

  const isPathActive = useCallback(
    (path: string): boolean => {
      return location.pathname === path;
    },
    [location.pathname]
  );

  const renderMenuItem = useCallback(
    (item: MenuItem, level = 0): React.ReactNode => {
      if (!isItemVisible(item)) {
        return null;
      }

      const hasChildren = Boolean(item.children && item.children.length > 0);
      const isExpanded = expandedItems.includes(item.text);
      const active = Boolean(item.path && isPathActive(item.path));

      return (
        <div key={item.text}>
          <MenuItemButton
            item={item}
            level={level}
            isActive={active}
            isExpanded={isExpanded}
            hasChildren={hasChildren}
            onClick={() => handleItemClick(item)}
          />

          {hasChildren && isExpanded && (
            <div className="mt-1 space-y-1">
              {item.children!.map((child) => renderMenuItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    },
    [expandedItems, handleItemClick, isItemVisible, isPathActive]
  );

  const renderUploadItems = useMemo(() => {
    if (isLoadingConfig) {
      return <UploadSkeletonItems />;
    }

    if (uploads.length === 0) {
      return null;
    }

    return uploads.map((upload) => (
      <UploadMenuItem
        key={`upload-${upload.doc_type}`}
        upload={upload}
        isActive={activeUploadDocType === upload.doc_type}
        onNavigate={handleUploadNavigate}
      />
    ));
  }, [isLoadingConfig, uploads, activeUploadDocType, handleUploadNavigate]);

  const menuContent = useMemo(() => {
    return menuItems.map((item, index) => {
      const menuItem = renderMenuItem(item);

      if (index === 0) {
        return (
          <React.Fragment key={item.text}>
            {menuItem}
            {renderUploadItems}
          </React.Fragment>
        );
      }

      return menuItem;
    });
  }, [renderMenuItem, renderUploadItems]);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-full w-64 border-r border-[hsl(214,32%,91%)] bg-white transition-transform duration-200',
          'md:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center justify-between border-b border-[hsl(214,32%,91%)] px-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[hsl(210,35%,32%)] to-[hsl(210,40%,25%)] flex items-center justify-center">
                <img
                  src="/favicon.png"
                  alt="Logo"
                  className="w-5 h-5 object-contain"
                />
              </div>
              <h2 className="text-lg font-semibold text-[hsl(210,50%,10%)]">
                Dashboard
              </h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="md:hidden text-[hsl(210,50%,10%)] hover:bg-[hsl(210,40%,96%)]"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="p-4">
            <OpsSelector />
          </div>

          <Separator className="bg-[hsl(214,32%,91%)]" />

          <nav className="flex-1 overflow-auto p-2">
            <div className="space-y-1">{menuContent}</div>
          </nav>
        </div>
      </aside>
    </>
  );
}
